import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { History, RotateCcw, Clock, User } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface MCQVersionHistoryProps {
  mcqId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface VersionRow {
  id: string;
  mcq_id: string;
  version_number: number;
  snapshot: any;
  changed_by: string | null;
  change_summary: string | null;
  created_at: string;
}

const FIELDS: Array<{ key: string; label: string }> = [
  { key: "stem", label: "Stem" },
  { key: "option_a", label: "Option A" },
  { key: "option_b", label: "Option B" },
  { key: "option_c", label: "Option C" },
  { key: "option_d", label: "Option D" },
  { key: "option_e", label: "Option E" },
  { key: "correct_option", label: "Correct" },
  { key: "explanation", label: "Explanation" },
  { key: "difficulty", label: "Difficulty" },
  { key: "lifecycle_status", label: "Lifecycle" },
];

export const MCQVersionHistory = ({ mcqId, open, onOpenChange }: MCQVersionHistoryProps) => {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: versions = [], isLoading } = useQuery({
    queryKey: ["mcq-versions", mcqId],
    enabled: open && !!mcqId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mcq_versions")
        .select("*")
        .eq("mcq_id", mcqId)
        .order("version_number", { ascending: false });
      if (error) throw error;
      return (data ?? []) as VersionRow[];
    },
  });

  const { data: currentMcq } = useQuery({
    queryKey: ["mcq-current", mcqId],
    enabled: open && !!mcqId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mcqs")
        .select("*")
        .eq("id", mcqId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const selected = useMemo(
    () => versions.find((v) => v.id === selectedId) ?? versions[0] ?? null,
    [versions, selectedId]
  );

  const revertMutation = useMutation({
    mutationFn: async (snapshot: any) => {
      const payload = {
        stem: snapshot.stem,
        option_a: snapshot.option_a,
        option_b: snapshot.option_b,
        option_c: snapshot.option_c,
        option_d: snapshot.option_d,
        option_e: snapshot.option_e,
        correct_option: snapshot.correct_option,
        explanation: snapshot.explanation,
        difficulty: snapshot.difficulty,
        tags: snapshot.tags,
        stem_image_url: snapshot.stem_image_url,
        explanation_image_url: snapshot.explanation_image_url,
        lifecycle_status: snapshot.lifecycle_status,
      };
      const { error } = await supabase.from("mcqs").update(payload).eq("id", mcqId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Reverted", description: "MCQ restored to selected version." });
      qc.invalidateQueries({ queryKey: ["mcq-versions", mcqId] });
      qc.invalidateQueries({ queryKey: ["mcq-current", mcqId] });
      qc.invalidateQueries({ queryKey: ["all-mcqs"] });
      qc.invalidateQueries({ queryKey: ["mcqs"] });
    },
    onError: (e: any) => {
      toast({ title: "Revert failed", description: e.message, variant: "destructive" });
    },
  });

  const renderValue = (v: any) => {
    if (v === null || v === undefined || v === "") return <span className="text-muted-foreground italic">empty</span>;
    return <span className="whitespace-pre-wrap break-words">{String(v)}</span>;
  };

  const isDifferent = (key: string) => {
    if (!selected || !currentMcq) return false;
    return selected.snapshot?.[key] !== (currentMcq as any)?.[key];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            MCQ Version History
          </DialogTitle>
          <DialogDescription>
            Compare any prior version with the current content and revert if needed. Snapshots are taken automatically on every meaningful edit.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>No prior versions yet. The first edit will create a snapshot.</p>
          </div>
        ) : (
          <div className="grid grid-cols-[280px_1fr] gap-4 flex-1 min-h-0">
            {/* Version list */}
            <div className="border rounded-lg overflow-hidden flex flex-col">
              <div className="bg-muted px-3 py-2 text-sm font-semibold border-b">
                {versions.length} version{versions.length !== 1 ? "s" : ""}
              </div>
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {versions.map((v) => {
                    const isActive = (selected?.id ?? versions[0]?.id) === v.id;
                    return (
                      <button
                        key={v.id}
                        onClick={() => setSelectedId(v.id)}
                        className={cn(
                          "w-full text-left p-2 rounded-md border transition-colors",
                          isActive ? "bg-primary/10 border-primary/40" : "hover:bg-muted border-transparent"
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <Badge variant="outline" className="font-mono text-xs">
                            v{v.version_number}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(v.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        {v.change_summary && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {v.change_summary}
                          </p>
                        )}
                        {v.changed_by && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                            <User className="h-3 w-3" />
                            <span className="truncate font-mono">{v.changed_by.slice(0, 8)}</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>

            {/* Diff panel */}
            <div className="border rounded-lg overflow-hidden flex flex-col">
              <div className="bg-muted px-4 py-2 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">
                    {selected ? `Version ${selected.version_number}` : "Select a version"}
                  </span>
                  <span className="text-xs text-muted-foreground">vs Current</span>
                </div>
                {selected && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" disabled={revertMutation.isPending}>
                        <RotateCcw className="h-3 w-3 mr-2" />
                        Revert to v{selected.version_number}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Revert MCQ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will replace the current content with version {selected.version_number}. The current state will be saved as a new snapshot first.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => revertMutation.mutate(selected.snapshot)}>
                          Revert
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
              <ScrollArea className="flex-1 p-4">
                {selected ? (
                  <div className="space-y-4">
                    {FIELDS.map((f) => {
                      const diff = isDifferent(f.key);
                      return (
                        <div
                          key={f.key}
                          className={cn(
                            "border rounded-md overflow-hidden",
                            diff && "border-amber-500/50"
                          )}
                        >
                          <div className="px-3 py-1.5 bg-muted/50 text-xs font-medium flex items-center justify-between">
                            <span>{f.label}</span>
                            {diff && (
                              <Badge variant="outline" className="text-xs border-amber-500 text-amber-600 dark:text-amber-400">
                                changed
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 divide-x text-sm">
                            <div className={cn("p-3", diff && "bg-red-500/5")}>
                              <div className="text-xs text-muted-foreground mb-1">v{selected.version_number}</div>
                              {renderValue(selected.snapshot?.[f.key])}
                            </div>
                            <div className={cn("p-3", diff && "bg-green-500/5")}>
                              <div className="text-xs text-muted-foreground mb-1">Current</div>
                              {renderValue((currentMcq as any)?.[f.key])}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    Select a version on the left.
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
